import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../store';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { numberFormatter } from '../../../utils/numbers';
import { replaceArgsInMessage } from '../../../utils/strings';
import { dateToStringFormat } from '@common/utils/date';
import { CrossCloseIcon, MissionsIcon, RobotIcon, TimeIcon } from '@common/utils/icons/icons';
import SoftwareVersionIndicator from './SoftwareVersionIndicator';
import StatsMissionSet from '../../../models/service/stats/mission-set';
import { toggleBuildNumberAndVersion } from '../../../store/service/service-slice';

const BuildNumberInfo: React.FC<{
  buildSet: StatsMissionSet;
  multiple: boolean;
  repeatedDeploymentIds: string[] | null;
}> = ({ buildSet, multiple, repeatedDeploymentIds }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const closeBuildNumberClickHandler = () => {
    dispatch(
      toggleBuildNumberAndVersion({
        buildNumber: buildSet.buildNumber,
        softwareVersion: buildSet.softwareVersion,
      }),
    );
  };

  return (
    <React.Fragment>
      <Grid item xs={12}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h2">{buildSet.buildNumber}</Typography>

          <IconButton onClick={closeBuildNumberClickHandler}>
            <CrossCloseIcon />
          </IconButton>
        </Stack>
      </Grid>

      {buildSet.rest.getStatus === HttpFetchStatus.Failed ||
      (buildSet.rest.getStatus === HttpFetchStatus.Loading && buildSet.data.length === 0) ? (
        <Grid item xs={12} p={2}>
          {buildSet.rest.getStatus === HttpFetchStatus.Failed ? (
            <Alert severity="error">{t('error-loading-missions')}</Alert>
          ) : (
            <Alert severity="info" icon={<CircularProgress size={24} />}>
              {t('loading-missions')}
            </Alert>
          )}
        </Grid>
      ) : (
        <React.Fragment>
          <Grid item xs={12} container spacing={2}>
            <Grid item xs={multiple ? 12 : 6}>
              <Stack direction="row" gap={2} alignItems="center">
                <RobotIcon color="info" />
                <Typography>
                  {replaceArgsInMessage(t('info-total-robots'), [
                    numberFormatter(
                      Array.from(
                        new Set(
                          buildSet.data
                            .filter(
                              (e) =>
                                repeatedDeploymentIds == null ||
                                repeatedDeploymentIds.includes(e.deploymentid),
                            )
                            .map((e) => e.robotid),
                        ),
                      ).length,
                      2,
                    ),
                  ])}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={multiple ? 12 : 6}>
              <Stack direction="row" gap={1}>
                <MissionsIcon color="info" />
                <Typography>
                  {replaceArgsInMessage(t('info-missions-and-runs'), [
                    numberFormatter(buildSet.count, 2),
                  ])}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={multiple ? 12 : 6}>
              <Stack direction="row" gap={1}>
                <TimeIcon color="info" />
                <Typography>
                  {buildSet.start != null &&
                    buildSet.end != null &&
                    replaceArgsInMessage(t('info-run-reports-timestamp'), [
                      dateToStringFormat(buildSet.start, true),
                      dateToStringFormat(buildSet.end, true),
                    ])}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={multiple ? 12 : 6}>
              <Stack direction="row" gap={1} alignItems="center">
                <Box px={0.5}>
                  <SoftwareVersionIndicator softwareVersion={buildSet.softwareVersion} />
                </Box>
                <Typography>
                  {replaceArgsInMessage(t('info-run-reports-software-version'), [
                    buildSet.softwareVersion,
                  ])}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default BuildNumberInfo;
